import React, {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {salesforceApiService, SalesforceSaveConfigPayload} from '../services/salesforceApi'

interface SalesforceConfigModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const SalesforceConfigModal: FC<SalesforceConfigModalProps> = ({show, onClose, onSuccess}) => {
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [securityToken, setSecurityToken] = useState('')
  const [username, setUsername] = useState('')
  const [instanceUrl, setInstanceUrl] = useState('')
  const [password, setPassword] = useState('')
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    setError(null)
    setLoading(true)
    salesforceApiService
      .getConfig()
      .then((config) => {
        if (config) {
          setClientId(config.client_id || '')
          setUsername(config.username || '')
          setInstanceUrl(config.instance_url || '')
          setClientSecret('')
          setSecurityToken('')
          setPassword('')
          setHasExistingConfig(true)
        } else {
          setClientId('')
          setClientSecret('')
          setSecurityToken('')
          setUsername('')
          setInstanceUrl('')
          setPassword('')
          setHasExistingConfig(false)
        }
      })
      .catch(() => setError('Erro ao carregar configuração'))
      .finally(() => setLoading(false))
  }, [show])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cid = clientId.trim()
    const user = username.trim()
    const url = instanceUrl.trim()
    const secret = clientSecret.trim()
    const token = securityToken.trim()
    const pwd = password.trim()

    if (!cid || !user || !url) {
      setError('Preencha Client ID, Username e URL da instância.')
      return
    }
    if (!hasExistingConfig && (!secret || !token || !pwd)) {
      setError('Na primeira configuração, todos os campos são obrigatórios.')
      return
    }

    const payload: SalesforceSaveConfigPayload = {
      client_id: cid,
      username: user,
      instance_url: url.replace(/\/+$/, ''),
    }
    if (secret) payload.client_secret = secret
    if (token) payload.security_token = token
    if (pwd) payload.password = pwd

    setSaving(true)
    setError(null)
    try {
      await salesforceApiService.saveConfig(payload)
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : err instanceof Error
            ? err.message
            : 'Não foi possível salvar a configuração'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  if (!show) return null

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Configurar Salesforce</h5>
            <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar' />
          </div>
          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {loading ? (
                <div className='text-center py-5'>
                  <span className='spinner-border spinner-border-sm' />
                </div>
              ) : (
                <>
                  <div className='alert alert-info py-3 mb-4'>
                    Informe a URL da sua instância Salesforce, as credenciais da Connected App e
                    do usuário. O security token é concatenado à senha na autenticação OAuth
                    (password grant).
                  </div>
                  {error && (
                    <div className='alert alert-danger' role='alert'>
                      {error}
                    </div>
                  )}
                  <div className='row g-3'>
                    <div className='col-12'>
                      <label className='form-label required'>URL da instância Salesforce</label>
                      <input
                        type='url'
                        className='form-control form-control-sm'
                        value={instanceUrl}
                        onChange={(e) => setInstanceUrl(e.target.value)}
                        placeholder='https://sua-empresa.my.salesforce.com'
                        autoComplete='off'
                      />
                      <div className='form-text'>
                        Ex.: <code>https://sua-empresa.my.salesforce.com</code>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>Client ID</label>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        autoComplete='off'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>Client Secret</label>
                      <input
                        type='password'
                        className='form-control form-control-sm'
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        placeholder={hasExistingConfig ? 'Deixe em branco para manter' : ''}
                        autoComplete='new-password'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>Token (Security Token)</label>
                      <input
                        type='password'
                        className='form-control form-control-sm'
                        value={securityToken}
                        onChange={(e) => setSecurityToken(e.target.value)}
                        placeholder={hasExistingConfig ? 'Deixe em branco para manter' : ''}
                        autoComplete='off'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>Username (e-mail)</label>
                      <input
                        type='email'
                        className='form-control form-control-sm'
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete='off'
                      />
                    </div>
                    <div className='col-12'>
                      <label className='form-label required'>Senha</label>
                      <input
                        type='password'
                        className='form-control form-control-sm'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={hasExistingConfig ? 'Deixe em branco para manter' : ''}
                        autoComplete='new-password'
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={onClose}>
                Cancelar
              </button>
              <button type='submit' className='btn btn-info' disabled={loading || saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SalesforceConfigModal
